import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAdTypeQuery, useCreateAdTypeMutation, useUpdateAdTypeMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

interface Feature {
  en: string;
  ar: string;
}

const AdminAdTypeForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetAdTypeQuery(id!, { skip: !isEdit });
  const [createAdType, { isLoading: isCreating }] = useCreateAdTypeMutation();
  const [updateAdType, { isLoading: isUpdating }] = useUpdateAdTypeMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    is_free: false,
    images_limit: 0,
    duration_days: 0,
    status: true,
    placement: '',
    ordering: '',
    duration: '',
    editable: '',
    analytics_available: false,
    targeting_available: false,
    notification_sent: false,
    external_links_allowed: false,
  });

  useEffect(() => {
    if (isEdit && data?.data) {
      const type = data.data;
      setFormData({
        title: type.title || '',
        title_ar: type.title_ar || '',
        is_free: (type.is_free == 1) ? true : false,
        images_limit: type.images_limit || 0,
        duration_days: type.duration_days || 0,
        status: (type.status == 1) ? true : false,
        placement: type.placement || '',
        ordering: type.ordering || '',
        duration: type.duration || '',
        editable: type.editable || '',
        analytics_available: (type.analytics_available == 1) ? true : false,
        targeting_available: (type.targeting_available == 1) ? true : false,
        notification_sent: (type.notification_sent == 1) ? true : false,
        external_links_allowed: (type.external_links_allowed == 1) ? true : false,
      });
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedTitle = formData.title.trim();
    const trimmedTitleAr = formData.title_ar.trim();
    
    if (!trimmedTitle || trimmedTitle.length < 3) {
      showError(t('Title is required and must be at least 3 characters long.'));
      return;
    }
    
    if (!trimmedTitleAr || trimmedTitleAr.length < 3) {
      showError(t('Title Arabic is required and must be at least 3 characters long.'));
      return;
    }

    try {
      const submitData = {
        title: trimmedTitle,
        title_ar: trimmedTitleAr,
        is_free: formData.is_free,
        images_limit: formData.images_limit,
        duration_days: formData.duration_days,
        status: formData.status,
        placement: formData.placement,
        ordering: formData.ordering,
        duration: formData.duration,
        editable: formData.editable,
        analytics_available: formData.analytics_available,
        targeting_available: formData.targeting_available,
        notification_sent: formData.notification_sent,
        external_links_allowed: formData.external_links_allowed,
      };

      if (isEdit) {
        const result = await updateAdType({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Ad Type updated successfully!'));
      } else {
        const result = await createAdType(submitData).unwrap();
        showSuccess(result.message || t('Ad Type created successfully!'));
      }
      
      navigate('/admin/ad-types');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || t('Validation failed'));
      } else {
        showError(err?.data?.message || t(isEdit ? 'Failed to update ad type. Please try again.' : 'Failed to create ad type. Please try again.'));
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Ad Type') : t('Add New Ad Type')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-types'); }} className="text-muted text-hover-primary">{t('Ad Types')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Ad Type Title')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Ad Type Title in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label htmlFor="is_free" className="fs-6 fw-semibold mb-5">{t('Mark as Free')}</label>
                        <div className="form-check form-switch">
                          <label className="form-check-label">{t('Free')} </label>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.is_free === true}
                            name="is_free"
                            onChange={handleInputChange}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                    </div>
                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-5">{t('Status')}</label>
                      <div className="form-check form-switch">
                        <label className="form-check-label">{formData.status ? t('Active') : t('Inactive')}</label>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.status === true}
                          name="status"
                          onChange={handleInputChange}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Images Limit')}</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Images Limit')}
                        name="images_limit"
                        value={formData.images_limit}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-3 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-5">{t('Analytics Available')}</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.analytics_available === true}
                          name="analytics_available"
                          onChange={handleInputChange}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-3 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-5">{t('Targeting Available')}</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.targeting_available === true}
                          name="targeting_available"
                          onChange={handleInputChange}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-3 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-5">{t('Notification Sent')}</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.notification_sent === true}
                          name="notification_sent"
                          onChange={handleInputChange}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-3 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-5">{t('External Links Allowed')}</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.external_links_allowed === true}
                          name="external_links_allowed"
                          onChange={handleInputChange}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Duration Days')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('e.g. 15 to 30 days')}
                        name="duration_days"
                        value={formData.duration_days}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Placement')}</label>
                      <select
                        className="form-select form-select-solid"
                        name="placement"
                        value={formData.placement}
                        onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                        required
                      >
                        <option value="">{t('Select Placement')}</option>
                        <option value="relevant_section">{t('Relevant Section')}</option>
                        <option value="premium">{t('Premium')}</option>
                        <option value="strategic">{t('Strategic')}</option>
                      </select>
                    </div>

                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Ordering')}</label>
                      <select
                        className="form-select form-select-solid"
                        name="ordering"
                        value={formData.ordering}
                        onChange={(e) => setFormData({ ...formData, ordering: e.target.value })}
                        required
                      >
                        <option value="">{t('Select Ordering')}</option>
                        <option value="chronological">{t('Chronological')}</option>
                        <option value="priority">{t('Priority')}</option>
                        <option value="prominent">{t('Prominent')}</option>
                      </select>
                    </div>


                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Editable')}</label>
                      <select
                        className="form-select form-select-solid"
                        name="editable"
                        value={formData.editable}
                        onChange={(e) => setFormData({ ...formData, editable: e.target.value })}
                        required
                      >
                        <option value="">{t('Select Editable Option')}</option>
                        <option value="once">{t('Once Only')}</option>
                        <option value="flexible">{t('Flexible')}</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <div className="row g-9">
                        
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/ad-types')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdTypeForm;

